/**
 * The one undo.
 *
 * DELIBERATELY NOT A NOTIFICATION CENTRE. `optimistic.ts` offers exactly one
 * undo — the most recent action, inside a ten-second window — because a stack
 * of them is a thing nobody reads and everybody mistrusts. This renders that
 * one, and disappears when it expires.
 *
 * `undoable` IS READ, NOT INFERRED. A move is briefly not undoable, between the
 * optimistic update and the server's COPYUID, and permanently not undoable if
 * that COPYUID never came. Rendering a live button in either case would offer
 * to move a message nobody can identify — so the button is disabled and says
 * why, rather than existing and failing.
 */
import { useEffect, useState } from 'react'
import type { ToastState } from './model/optimistic.ts'

export function Toast({
  toast,
  onUndo,
  onDismiss,
}: {
  toast: ToastState | null
  onUndo: () => void
  onDismiss: () => void
}): React.JSX.Element | null {
  const [, tick] = useState(0)

  // The window closes on its own. Re-rendering once a second is what makes the
  // toast vanish without the caller having to poll the queue.
  useEffect(() => {
    if (!toast) return
    const timer = setInterval(() => tick((n) => n + 1), 500)
    return () => clearInterval(timer)
  }, [toast])

  if (!toast) return null
  const remaining = toast.expiresAt - Math.floor(Date.now() / 1000)
  if (remaining <= 0) return null

  return (
    <div className="toast" role="status" aria-live="polite">
      <span className="toast-label">{toast.label}</span>
      <button type="button" className="toast-undo" onClick={onUndo} disabled={!toast.undoable}>
        {toast.undoable ? 'Undo' : 'Undo unavailable'}
      </button>
      <button type="button" className="toast-close" onClick={onDismiss} aria-label="Dismiss">
        ×
      </button>
    </div>
  )
}
